import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Search, Sparkles, Shuffle, CloudRain, Sun } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import FilterSidebar from "@/components/FilterSidebar";
import ActivityCard from "@/components/ActivityCard";
import ActivityModal from "@/components/ActivityModal";
import PlannerBar from "@/components/PlannerBar";
import AiAssistant from "@/components/AiAssistant";
import PrintPlan from "@/components/PrintPlan";
import { TRAJANJA, CILJ_MINUTA, DANI } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const EMPTY_FILTERS = {
  q: "",
  razredi: [],
  podrucja: [],
  trajanja: [],
  mjesta: [],
  buka: [],
  grupa: "",
  sezona: "",
  bezMaterijala: false,
  kisniDan: false,
  suncanDan: false,
};

const EMPTY_WEEK = { pon: [], uto: [], sri: [], cet: [], pet: [] };

let planCounter = 0;

export default function App() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState("zadano");
  const [weekPlan, setWeekPlan] = useState(EMPTY_WEEK);
  const [activeDay, setActiveDay] = useState("pon");
  const [selected, setSelected] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [planPulse, setPlanPulse] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/activities`)
      .then((r) => setActivities(r.data.activities))
      .catch(() => toast.error("Greška pri učitavanju aktivnosti."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    let out = activities.filter((a) => {
      if (q) {
        const hay = `${a.naziv} ${a.opis} ${a.podrucje} ${a.materijali.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.razredi.length && !a.razredi.some((r) => filters.razredi.includes(r))) return false;
      if (filters.podrucja.length && !filters.podrucja.includes(a.podrucje)) return false;
      if (filters.trajanja.length) {
        const ok = filters.trajanja.some((id) => TRAJANJA.find((t) => t.id === id)?.test(a.trajanje));
        if (!ok) return false;
      }
      if (filters.mjesta.length && !filters.mjesta.includes(a.mjesto)) return false;
      if (filters.buka.length && !filters.buka.includes(a.razina_buke)) return false;
      if (filters.grupa && a.velicina_grupe !== "Bilo koja" && a.velicina_grupe !== filters.grupa) return false;
      if (filters.sezona && a.sezona !== filters.sezona) return false;
      if (filters.bezMaterijala && a.materijali.length > 0) return false;
      if (filters.kisniDan && (a.mjesto !== "Učionica" || a.razina_buke === "Glasno")) return false;
      if (filters.suncanDan && a.mjesto !== "Dvorište" && a.podrucje !== "Sportsko-rekreativne") return false;
      return true;
    });
    if (sort === "trajanje-asc") out = [...out].sort((a, b) => a.trajanje - b.trajanje);
    if (sort === "trajanje-desc") out = [...out].sort((a, b) => b.trajanje - a.trajanje);
    if (sort === "naziv") out = [...out].sort((a, b) => a.naziv.localeCompare(b.naziv, "hr"));
    return out;
  }, [activities, filters, sort]);

  const dayPlan = weekPlan[activeDay];
  const totalMin = dayPlan.reduce((s, p) => s + p.trajanje, 0);
  const weekCount = DANI.reduce((s, d) => s + weekPlan[d.id].length, 0);
  const dayLabel = DANI.find((d) => d.id === activeDay).label;

  const addToPlan = (activity) => {
    setWeekPlan((prev) => ({ ...prev, [activeDay]: [...prev[activeDay], { ...activity, planId: `p${++planCounter}` }] }));
    setPlanPulse((n) => n + 1);
    toast.success(`„${activity.naziv}" dodana u ${dayLabel.toLowerCase()}`, {
      description: `${dayLabel}: ${totalMin + activity.trajanje} od ${CILJ_MINUTA} min`,
    });
  };

  const removeFromPlan = (day, planId) =>
    setWeekPlan((prev) => ({ ...prev, [day]: prev[day].filter((p) => p.planId !== planId) }));

  const clearDay = () => setWeekPlan((prev) => ({ ...prev, [activeDay]: [] }));

  const surpriseMe = () => {
    const pool = filtered.length ? filtered : activities;
    if (!pool.length) return;
    setSelected(pool[Math.floor(Math.random() * pool.length)]);
  };

  const autoFillPlan = () => {
    const pool = [...filtered].sort(() => Math.random() - 0.5);
    const next = [...dayPlan];
    let total = totalMin;
    for (const a of pool) {
      if (total >= CILJ_MINUTA - 10) break;
      if (total + a.trajanje <= CILJ_MINUTA && !next.some((p) => p.id === a.id)) {
        next.push({ ...a, planId: `p${++planCounter}` });
        total += a.trajanje;
      }
    }
    if (next.length === dayPlan.length) {
      toast.info("Nema dovoljno aktivnosti u trenutnim rezultatima za dopunu plana.");
      return;
    }
    setWeekPlan((prev) => ({ ...prev, [activeDay]: next }));
    setPlanPulse((n) => n + 1);
    toast.success(`${dayLabel} dopunjen — ukupno ${total} min`);
  };

  return (
    <div className="w-full h-auto overflow-y-auto">
      <div id="app-root" className="min-h-screen bg-[#F8FAFC] pb-28 w-full">
        <Header planCount={weekCount} onOpenAi={() => setAiOpen(true)} />

        <section className="max-w-4xl mx-auto px-6 pt-12 pb-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 fade-up">
            Ideje za slobodno vrijeme<br className="hidden sm:block" />
            <span className="text-indigo-600"> koje djeca pamte</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto fade-up" style={{ animationDelay: "0.1s" }}>
            111+ provjerenih aktivnosti za dva sata organiziranog slobodnog vremena
            u produženom boravku. Filtrirajte, složite plan od 120 minuta i isprintajte ga.
          </p>
          <div className="mt-8 relative max-w-2xl mx-auto fade-up" style={{ animationDelay: "0.2s" }}>
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              data-testid="search-input"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Pretraži aktivnosti po nazivu, opisu ili materijalu…"
              className="w-full rounded-full border border-slate-200 bg-white py-4 pl-13 pr-4 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              style={{ paddingLeft: "3.25rem" }}
            />
          </div>
          <div className="mt-4 flex items-center justify-center gap-6 flex-wrap">
            <button
              data-testid="open-ai-hero-btn"
              onClick={() => setAiOpen(true)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
            >
              <Sparkles className="h-4 w-4" /> Nemate ideju? Pitajte AI asistenta
            </button>
            <button
              data-testid="surprise-me-btn"
              onClick={surpriseMe}
              className="inline-flex items-center gap-2 text-sm font-semibold text-amber-600 hover:text-amber-800 transition-colors duration-200"
            >
              <Shuffle className="h-4 w-4" /> Iznenadi me!
            </button>
            <button
              data-testid="rainy-day-btn"
              onClick={() => setFilters((f) => ({ ...f, kisniDan: !f.kisniDan, suncanDan: false }))}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-200 ${
                filters.kisniDan
                  ? "bg-sky-600 text-white hover:bg-sky-700"
                  : "bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100"
              }`}
            >
              <CloudRain className="h-4 w-4" /> Kišni dan{filters.kisniDan ? " ✓" : ""}
            </button>
            <button
              data-testid="sunny-day-btn"
              onClick={() => setFilters((f) => ({ ...f, suncanDan: !f.suncanDan, kisniDan: false }))}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-200 ${
                filters.suncanDan
                  ? "bg-amber-500 text-white hover:bg-amber-600"
                  : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
              }`}
            >
              <Sun className="h-4 w-4" /> Sunčan dan{filters.suncanDan ? " ✓" : ""}
            </button>
          </div>
        </section>

        <main className="grid grid-cols-1 lg:grid-cols-12 gap-8 px-6 max-w-7xl mx-auto py-4">
          <aside className="lg:col-span-3">
            <button
              data-testid="toggle-filters-btn"
              onClick={() => setFiltersOpen((o) => !o)}
              className="lg:hidden w-full mb-3 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors duration-150"
            >
              {filtersOpen ? "Sakrij filtere ▲" : "Prikaži filtere ▼"}
            </button>
            <div className={`${filtersOpen ? "block" : "hidden"} lg:block`}>
              <FilterSidebar filters={filters} setFilters={setFilters} onReset={() => setFilters(EMPTY_FILTERS)} />
            </div>
          </aside>

          <section className="lg:col-span-9">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm font-medium text-slate-500" data-testid="results-count">
                <span className="text-slate-900 font-bold">{filtered.length}</span>{" "}
                {filtered.length === 1 ? "aktivnost" : filtered.length < 5 && filtered.length > 0 ? "aktivnosti" : "aktivnosti"}
              </p>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="zadano">Preporučeno</option>
                <option value="trajanje-asc">Najkraće prvo</option>
                <option value="trajanje-desc">Najduže prvo</option>
                <option value="naziv">Naziv A–Ž</option>
              </select>
            </div>

            {loading ? (
              <p className="text-slate-500 py-16 text-center">Učitavanje aktivnosti…</p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="empty-state">
                <p className="text-lg font-bold text-slate-800">Nema rezultata za odabrane filtere</p>
                <p className="text-sm text-slate-500 mt-2">Pokušajte ukloniti neki filter ili pitajte AI asistenta za novu ideju.</p>
                <Button data-testid="empty-ai-btn" onClick={() => setAiOpen(true)} className="mt-6 rounded-full bg-indigo-600 hover:bg-indigo-700">
                  <Sparkles className="h-4 w-4 mr-2" /> Generiraj ideju AI-jem
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((a) => (
                  <ActivityCard key={a.id} activity={a} onOpen={() => setSelected(a)} onAdd={() => addToPlan(a)} />
                ))}
              </div>
            )}
          </section>
        </main>

        <PlannerBar
          weekPlan={weekPlan}
          activeDay={activeDay}
          setActiveDay={setActiveDay}
          pulseKey={planPulse}
          onRemove={removeFromPlan}
          onClearDay={clearDay}
          onAutoFill={autoFillPlan}
          onPrint={() => window.print()}
        />

        <ActivityModal
          activity={selected}
          onClose={() => setSelected(null)}
          onAdd={(a) => { addToPlan(a); setSelected(null); }}
          api={API}
        />

        <AiAssistant open={aiOpen} onClose={() => setAiOpen(false)} onAdd={addToPlan} api={API} />
        <Toaster position="bottom-center" richColors />
      </div>

      <PrintPlan weekPlan={weekPlan} />
    </div>
  );
}
